// Muscle-map SVG data — extracted VERBATIM from `fierro-gym.html`
// (the `MUSCLE_SHAPES`, `mirror(p)` and `bodyFigure(view)` definitions).
//
// Every coordinate is copied exactly from the source; nothing is rounded,
// simplified, or redrawn. The figures live in a `0 0 120 205` viewBox.
//
// Source `mirror(p)` helper (line 656 of fierro-gym.html):
//   function mirror(p){ return p + '<g transform="translate(120,0) scale(-1,1)">' + p + '</g>'; }
// i.e. it draws the base fragment `p` once, then a second copy flipped
// horizontally across x = 60 via `translate(120,0) scale(-1,1)`.

export type MuscleView = 'f' | 'b'

// Per-muscle tappable shape.
// - `mirror: true`  → `d` holds the SINGLE base fragment; the renderer draws
//   `d` PLUS a mirrored copy (see MIRROR_TRANSFORM), reproducing `mirror(d)`.
// - `mirror: false` → `d` holds the complete fragment exactly as the source
//   emits it (a centered single shape, or an already-expanded mix that
//   contains its own inline mirror group).
export interface MuscleShape {
  v: MuscleView
  d: string
  mirror?: boolean
}

// The exact horizontal-flip transform used by the source `mirror(p)` helper.
export const MIRROR_TRANSFORM = 'translate(120,0) scale(-1,1)'

// Module-private reproduction of the source `mirror(p)` helper. Used only to
// pre-expand fragments that the source itself mirrors inline (the abdomen
// obliques and the body-outline arms/legs). Produces byte-identical output to
// the original helper for the given transform.
const mir = (p: string): string =>
  p + '<g transform="' + MIRROR_TRANSFORM + '">' + p + '</g>'

// ---------------------------------------------------------------------------
// MUSCLE_SHAPES — muscle slug → tappable shape.
// Views come from the source `MUSCLES` map: hombro/pecho/abdomen/biceps/
// antebrazo/cuadriceps are front ('f'); espalda/trapecio/triceps/gluteo/
// isquios/pantorrilla are back ('b').
// ---------------------------------------------------------------------------
export const MUSCLE_SHAPES: Record<string, MuscleShape> = {
  // mirror('<path .../>') — deltoid, drawn on both sides.
  hombro: {
    v: 'f',
    mirror: true,
    d: '<path d="M37.5 31 C41.5 29 46 29.8 47.5 32.5 C48.2 36.5 46.5 40.5 43 42.5 C39 43.5 36 40.5 36 36.5 C36.2 34 36.8 32.2 37.5 31 Z"/>',
  },
  // mirror(...) — pec block + upper detail line, drawn on both sides.
  pecho: {
    v: 'f',
    mirror: true,
    d: '<path d="M47.5 35.5 C53 33.5 58.5 34.5 59.3 37.5 L59.3 50 C56.5 54.5 48.5 54 45.8 49 C44.2 44.5 45.5 38.5 47.5 35.5 Z"/><path d="M47 38 C52 36.8 57 37 59.3 38.5" fill="none" stroke-width="0.6" opacity="0.45"/>',
  },
  // MIXED in the source: a CENTERED rectus-abdominis block + six-pack lines
  // (NOT mirrored) concatenated with mirror(oblique). Stored fully expanded
  // with mirror:false so the renderer emits exactly what the source produced.
  abdomen: {
    v: 'f',
    mirror: false,
    d:
      '<path d="M51.5 56.5 C55.5 55 64.5 55 68.5 56.5 C70 66.5 69.2 78 67.2 86 C63.5 90.5 56.5 90.5 52.8 86 C50.8 78 50 66.5 51.5 56.5 Z"/><line x1="60" y1="57.5" x2="60" y2="88.5" stroke-width="0.6" opacity="0.5"/><line x1="53" y1="64" x2="67" y2="64" stroke-width="0.6" opacity="0.5"/><line x1="52.5" y1="72" x2="67.5" y2="72" stroke-width="0.6" opacity="0.5"/><line x1="53" y1="80" x2="67" y2="80" stroke-width="0.6" opacity="0.5"/>' +
      mir('<path d="M46 58 C48 57 49.5 57.6 49.7 59.5 C50 66.5 49.4 74.5 48.2 80.5 C46.3 79.5 45.2 74.5 45 68.5 C44.9 64.5 45.3 60.5 46 58 Z"/>'),
  },
  // mirror(...) — biceps peak + detail line, drawn on both sides.
  biceps: {
    v: 'f',
    mirror: true,
    d: '<path d="M30.5 44.5 C33.8 42.5 36.6 44.3 36.9 49 C37.2 56 35.7 63 32.9 66 C30.1 64.5 28.7 58 29 52 C29.2 48.5 29.7 46 30.5 44.5 Z"/><path d="M35.5 48 C36 54 35 60 33.5 63.5" fill="none" stroke-width="0.5" opacity="0.4"/>',
  },
  // mirror(...) — forearm + detail line, drawn on both sides.
  antebrazo: {
    v: 'f',
    mirror: true,
    d: '<path d="M26.8 69 C29 71 32.3 71 34.1 69.5 C33.6 77 32.6 84.5 31.6 91 C30.8 96.5 29.9 101 29 104 C26.6 103.5 24.7 101 24.2 96.5 C23.8 88.5 24.8 78.5 26.8 69 Z"/><path d="M31.5 72 C31 80 30 88 29 95" fill="none" stroke-width="0.5" opacity="0.4"/>',
  },
  // mirror(...) — quad sweep + two detail lines, drawn on both sides.
  cuadriceps: {
    v: 'f',
    mirror: true,
    d: '<path d="M44.5 97 C48.5 94.5 54 95.8 55.8 99.5 C57 111 56.6 126 54.6 138 C53 144 47.5 144.5 45.5 138.5 C43.2 126 43.4 109 44.5 97 Z"/><path d="M50.2 100 C51.2 113 51.2 127 50.2 137" fill="none" stroke-width="0.6" opacity="0.45"/><path d="M53.8 126 C54.8 132 54.2 138 52.5 141.5" fill="none" stroke-width="0.5" opacity="0.4"/>',
  },
  // Centered single shape in the source (no mirror), so mirror:false.
  espalda: {
    v: 'b',
    mirror: false,
    d: '<path d="M45 41 C50 39.5 54 41 57 45 C58.8 47.5 61.2 47.5 63 45 C66 41 70 39.5 75 41 C74 52.5 70.8 62.5 65.5 69.5 C62 73.5 58 73.5 54.5 69.5 C49.2 62.5 46 52.5 45 41 Z"/><line x1="60" y1="47.5" x2="60" y2="71" stroke-width="0.6" opacity="0.5"/><path d="M56.5 73.5 C58.8 72.8 61.2 72.8 63.5 73.5 C64 77.5 63.5 81.5 62.2 84.5 L57.8 84.5 C56.5 81.5 56 77.5 56.5 73.5 Z"/>',
  },
  // Centered single shape in the source (no mirror), so mirror:false.
  trapecio: {
    v: 'b',
    mirror: false,
    d: '<path d="M60 23 C63.5 25 68 27 71.5 29 C68.5 34.5 64.3 40 60 43.5 C55.7 40 51.5 34.5 48.5 29 C52 27 56.5 25 60 23 Z"/><line x1="60" y1="25" x2="60" y2="42" stroke-width="0.5" opacity="0.4"/>',
  },
  // mirror(...) — triceps + detail line, drawn on both sides.
  triceps: {
    v: 'b',
    mirror: true,
    d: '<path d="M30.5 44.5 C33.8 42.5 36.6 44.3 36.9 49 C37.2 56 35.7 63 32.9 66 C30.1 64.5 28.7 58 29 52 C29.2 48.5 29.7 46 30.5 44.5 Z"/><path d="M30.5 48 C30 54.5 30.8 60.5 32.5 64" fill="none" stroke-width="0.5" opacity="0.4"/>',
  },
  // mirror('<path .../>') — glute, drawn on both sides.
  gluteo: {
    v: 'b',
    mirror: true,
    d: '<path d="M46 86 C51 82.5 58 84 59 89.5 C59.8 96 56.8 102.5 51.5 103.5 C46.5 103 43.6 98 44 92.5 C44.3 89.5 45 87.5 46 86 Z"/>',
  },
  // mirror(...) — hamstring + detail line, drawn on both sides.
  isquios: {
    v: 'b',
    mirror: true,
    d: '<path d="M45 107 C49 104.5 54 105.5 55.5 109 C56.8 120 55.8 132.5 53.5 140.5 C50.5 144.5 46.5 143.5 45.2 138.5 C43.2 127.5 43.8 116 45 107 Z"/><path d="M50 110 C50.8 121 50.5 132 49.5 139" fill="none" stroke-width="0.5" opacity="0.4"/>',
  },
  // mirror(...) — calf + detail line, drawn on both sides.
  pantorrilla: {
    v: 'b',
    mirror: true,
    d: '<path d="M46 150 C49 146.5 53.5 147.5 54.5 152 C55.5 160.5 54 170 51 175.5 C48 177.5 45.8 173 45 167 C44.2 161 45 154.5 46 150 Z"/><path d="M50 150 C50.5 157 50.3 164 49.5 170" fill="none" stroke-width="0.5" opacity="0.4"/>',
  },
}

// ---------------------------------------------------------------------------
// BODY_OUTLINE — the line-art body silhouette, per view.
//
// In the source, `bodyFigure(view)` wraps these elements in a single group:
//   <g fill="none" stroke="rgba(244,244,241,0.20)" stroke-width="1.2" stroke-linecap="round"> … </g>
// A faithful renderer should wrap BODY_OUTLINE[view] in that same group.
//
// The arms and legs elements are `mirror(...)` fragments (base + inline flipped
// copy) and are stored fully expanded here. The last element(s) are the
// view-specific detail lines (front: collar + pec split; back: spine).
// ---------------------------------------------------------------------------

// Shared silhouette elements (identical for both views).
const OUTLINE_HEAD = '<ellipse cx="60" cy="11.5" rx="7" ry="9"/>'
const OUTLINE_NECK =
  '<path d="M55.5 19.5 C55.5 22 55 24 54 25.5 M64.5 19.5 C64.5 22 65 24 66 25.5"/>'
const OUTLINE_CLAVICLE =
  '<path d="M54 25.5 C48.5 26.8 43.5 28.2 39.5 30.5 M66 25.5 C71.5 26.8 76.5 28.2 80.5 30.5"/>'
const OUTLINE_TORSO =
  '<path d="M39.5 30.5 C37.8 40 37.5 50 38.8 58.5 C39.8 65 41.5 71 43.8 76 C46 81 48.8 85.5 52 88.5 C54.5 91 57 92.5 60 93 C63 92.5 65.5 91 68 88.5 C71.2 85.5 74 81 76.2 76 C78.5 71 80.2 65 81.2 58.5 C82.5 50 82.2 40 80.5 30.5"/>'
const OUTLINE_ARMS = mir(
  '<path d="M38.8 31.5 C34 34 30.8 38.5 29.3 44.5 C27.8 51 26.8 58.5 26.2 65.5 M26.2 66.5 C25 74 23.8 82 23.2 90 C22.8 95 22.6 100 22.9 104.5"/><path d="M34.2 66.5 C33.4 73.5 32.4 80.5 31.4 87 C30.6 93 29.8 99 29 104.5"/><path d="M26.2 65.5 C28 68.5 32 69 34.2 66.8" opacity="0.6"/><ellipse cx="25.8" cy="110.5" rx="3.4" ry="5"/>',
)
const OUTLINE_LEGS = mir(
  '<path d="M44 95 C42.5 105 42 117 42.8 129 C43.4 139 44.7 148 46.1 155 M58.7 95 C59.2 105 59.4 117 58.9 129 C58.6 139 58 148 57 155"/><path d="M46.8 157 C49.8 158.8 53.6 158.8 56.4 157" opacity="0.6"/><path d="M46.4 159 C45.2 166 45 174 46 182 C46.7 188 47.5 192.5 48.2 196 M56.2 159 C56.9 166 56.9 175 55.9 183 C55.3 189 54.6 193 54 196"/><path d="M48.2 196 L47.6 200.5 L56.8 200.5 L54.2 196"/>',
)

const OUTLINE_SHARED = [
  OUTLINE_HEAD,
  OUTLINE_NECK,
  OUTLINE_CLAVICLE,
  OUTLINE_TORSO,
  OUTLINE_ARMS,
  OUTLINE_LEGS,
]

export const BODY_OUTLINE: Record<MuscleView, string[]> = {
  f: [
    ...OUTLINE_SHARED,
    '<path d="M46.5 32.5 C52 34 68 34 73.5 32.5" opacity="0.55"/>',
    '<path d="M51 88 L60 95.5 L69 88" opacity="0.55"/>',
  ],
  b: [...OUTLINE_SHARED, '<path d="M60 73.5 L60 88" opacity="0.55"/>'],
}
